import { STREAM_STYLE_LABELS, STREAM_STYLE_IDS } from './stream-themes';
import { AlertTriangle, Play, RefreshCw, RotateCcw, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  loadAlertLiveState,
  loadAlertsWorkspace,
  replayAlert,
  testAlert,
  updateAlertConfig,
  updateRuntimeModes,
  type AlertWorkspaceSnapshot,
  type AlertMotionMode,
  type NativeAlertType,
  type RecentAlert,
  type TestableAlertType,
} from './alerts-api';
import type { StreamEffectStyle } from './stream-api';
import './alerts.css';

const STYLE_LABELS = STREAM_STYLE_LABELS;
const MOTION_LABELS: Record<AlertMotionMode, string> = {
  classic: 'Classic',
  animated: 'Animated',
  maximum: 'Maximum',
};

const ALERTS: Array<{ type: NativeAlertType; label: string; description: string; testable: boolean }> = [
  { type: 'follow', label: 'Follow', description: 'New follower alert.', testable: true },
  { type: 'subscribe', label: 'Subscription', description: 'New subscription alert.', testable: true },
  { type: 'resubscribe', label: 'Resubscription', description: 'Returning subscriber with month count/message.', testable: true },
  { type: 'gift', label: 'Gift Subs', description: 'Gifted subscriptions and gift count.', testable: true },
  { type: 'cheer', label: 'Bits', description: 'Bits cheer and amount.', testable: true },
  { type: 'raid', label: 'Raid', description: 'Incoming raid and viewer count.', testable: true },
  { type: 'shoutout', label: 'Shoutout', description: 'FFSControl themed shoutout presentation.', testable: true },
  { type: 'redemption', label: 'Channel Points', description: 'Only the mapped exact custom reward enters the themed alert pipeline. Use the single Test Redemption button below.', testable: false },
];

function formatWhen(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString('en-GB') : '—';
}

function alertLabel(type: string): string {
  return ALERTS.find((item) => item.type === type)?.label ?? (type === 'viewer_sound' ? 'Viewer Sound' : type);
}

function alertProblem(alert: RecentAlert): string | null {
  return alert.error_message ?? alert.visual_error ?? alert.audio_error ?? null;
}

export function AlertsPage() {
  const [snapshot, setSnapshot] = useState<AlertWorkspaceSnapshot | null>(null);
  const [editStyle, setEditStyle] = useState<StreamEffectStyle>('black-yellow');
  const [historyFilter, setHistoryFilter] = useState<'all' | 'attention' | 'twitch' | 'simulation'>('all');
  const [historySearch, setHistorySearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [durationSeconds, setDurationSeconds] = useState('7');
  const editorInitialised = useRef(false);
  const livePollInFlight = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const next = await loadAlertsWorkspace();
      setSnapshot(next);
      if (!editorInitialised.current) {
        setEditStyle(next.selectedStyle);
        editorInitialised.current = true;
      }
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Alerts could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    let stopped = false;

    const pollLiveState = async () => {
      if (livePollInFlight.current) return;
      livePollInFlight.current = true;
      try {
        const live = await loadAlertLiveState();
        if (stopped) return;
        setSnapshot((current) => current ? { ...current, status: live.status, recent: live.recent } : current);
      } catch {
        // Keep the last known live queue/history state. Manual refresh surfaces full workspace errors.
      } finally {
        livePollInFlight.current = false;
      }
    };

    const timer = window.setInterval(() => void pollLiveState(), 3_000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, []);

  async function apply(changes: Parameters<typeof updateAlertConfig>[0], success: string) {
    setBusy(true);
    setMessage('');
    try {
      await updateAlertConfig(changes);
      setSnapshot(await loadAlertsWorkspace());
      setMessage(success);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Alert configuration could not be changed.');
    } finally {
      setBusy(false);
    }
  }

  async function applyModes(changes: Partial<Pick<AlertWorkspaceSnapshot, 'rehearsalEnabled' | 'safeModeEnabled' | 'suppressedFeatures'>>, success: string) {
    if (!snapshot) return;
    setBusy(true);
    setMessage('');
    try {
      await updateRuntimeModes({
        rehearsalEnabled: changes.rehearsalEnabled ?? snapshot.rehearsalEnabled,
        safeModeEnabled: changes.safeModeEnabled ?? snapshot.safeModeEnabled,
        suppressedFeatures: changes.suppressedFeatures ?? snapshot.suppressedFeatures,
      });
      setSnapshot(await loadAlertsWorkspace());
      setMessage(success);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Rehearsal mode could not be changed.');
    } finally {
      setBusy(false);
    }
  }

  async function runTest(type: TestableAlertType) {
    setBusy(true);
    setMessage('');
    try {
      await testAlert(type);
      setSnapshot(await loadAlertsWorkspace());
      setMessage(`${alertLabel(type)} simulation queued.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Alert simulation could not be queued.');
    } finally {
      setBusy(false);
    }
  }

  async function runReplay(alert: RecentAlert) {
    if (!window.confirm(`Replay ${alertLabel(alert.alert_type)} for ${alert.user_name ?? alert.user_login ?? 'this viewer'} as an isolated simulation?`)) return;
    setBusy(true);
    setMessage('');
    try {
      await replayAlert(alert.id);
      setSnapshot(await loadAlertsWorkspace());
      setMessage('Alert replay queued as a simulation.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Alert could not be replayed.');
    } finally {
      setBusy(false);
    }
  }

  const selectedDuration = snapshot?.config.styles.find((item) => item.style === editStyle)?.alertDurationMs ?? 7000;
  const selectedSlots = snapshot?.config.slots.filter((slot) => slot.style === editStyle) ?? [];
  const selectedMotionMode = snapshot?.config.styles.find((item) => item.style === 'tfi-friday')?.alertMotionMode ?? 'classic';
  const ambientShimmerEnabled = snapshot?.config.styles.find((item) => item.style === 'tfi-friday')?.ambientShimmerEnabled === true;
  const durationMs = Math.round(Number(durationSeconds) * 1000);
  const durationValid = Number.isFinite(durationMs) && durationMs >= 1000 && durationMs <= 30000 && durationMs % 500 === 0;
  const rehearsalReady = snapshot?.rehearsalEnabled === true && snapshot.rehearsalSuppressed === false;

  useEffect(() => {
    setDurationSeconds(String(selectedDuration / 1000));
  }, [editStyle, selectedDuration]);

  const visibleHistory = useMemo(() => {
    const query = historySearch.trim().toLocaleLowerCase('en-GB');
    return (snapshot?.recent ?? []).filter((alert) => {
      const hasProblem = alert.status === 'failed' || alert.status === 'partial' || alert.queue_status === 'failed' || Boolean(alertProblem(alert));
      if (historyFilter === 'attention' && !hasProblem) return false;
      if (historyFilter === 'twitch' && alert.simulated) return false;
      if (historyFilter === 'simulation' && !alert.simulated) return false;
      if (!query) return true;
      return `${alertLabel(alert.alert_type)} ${alert.user_name ?? ''} ${alert.user_login ?? ''} ${alert.status} ${alertProblem(alert) ?? ''}`.toLocaleLowerCase('en-GB').includes(query);
    });
  }, [historyFilter, historySearch, snapshot]);

  if (loading && !snapshot) return <div className="ffs-next-loading">Loading Alerts…</div>;

  return (
    <div className="ffs-workspace-page ffs-alerts-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}

      <section className="ffs-workspace-hero">
        <div><small>TWITCH PRESENTATION</small><h2>Alerts</h2><p>Configuration, live delivery health, safe testing and replay history in one workspace.</p></div>
        <div className="ffs-alert-hero-actions">
          <span className="ffs-alert-live-refresh">LIVE STATUS · 3s</span>
          <span className={`ffs-status ${snapshot?.status.overlayClients ? 'good' : 'warning'}`}>{snapshot?.status.overlayClients ?? 0} OVERLAY CLIENT{snapshot?.status.overlayClients === 1 ? '' : 'S'}</span>
          <button type="button" className={`ffs-button ${snapshot?.config.enabled ? 'primary' : 'warning'}`} disabled={busy || !snapshot} onClick={() => void apply({ enabled: !(snapshot?.config.enabled ?? false) }, snapshot?.config.enabled ? 'Automatic Twitch alerts disabled.' : 'Automatic Twitch alerts enabled.')}>{snapshot?.config.enabled ? 'Alerts On' : 'Alerts Off'}</button>
          <button type="button" className="ffs-button secondary" disabled={busy} onClick={() => void refresh()}><RefreshCw size={15} /> Refresh All</button>
        </div>
      </section>

      <section className="ffs-alert-count-grid">
        {(['queued', 'playing', 'completed', 'failed', 'skipped'] as const).map((key) => <div key={key} className={key === 'failed' && (snapshot?.status.counts[key] ?? 0) > 0 ? 'problem' : ''}><span>{key}</span><strong>{(snapshot?.status.counts[key] ?? 0).toLocaleString('en-GB')}</strong></div>)}
      </section>

      <section className="ffs-workspace-card ffs-alert-config-card">
        <header><div><small>STYLE CONFIGURATION</small><h3>Alert presentation</h3></div><span>Current stream style: <strong>{snapshot ? STYLE_LABELS[snapshot.selectedStyle] : '—'}</strong></span></header>
        <div className="ffs-alert-style-toolbar">
          <div className="ffs-alert-style-choices">{(STREAM_STYLE_IDS as StreamEffectStyle[]).map((style) => <button type="button" key={style} className={editStyle === style ? 'selected' : ''} onClick={() => setEditStyle(style)}>{STYLE_LABELS[style]}</button>)}</div>
          {editStyle === 'black-yellow' ? <div className="ffs-black-yellow-overlay-note"><strong>Dedicated OBS renderer</strong><span>/effects/alerts/black-yellow · 1371 × 74</span></div> : null}
          {editStyle === 'tfi-friday' ? <div className="ffs-tfi-friday-controls"><div className="ffs-tfi-motion-control"><span>Friday spectacle</span><div className="ffs-tfi-motion-choices">{(['classic', 'animated', 'maximum'] as AlertMotionMode[]).map((mode) => <button type="button" key={mode} className={selectedMotionMode === mode ? 'selected' : ''} disabled={busy} onClick={() => void apply({ style: 'tfi-friday', alertMotionMode: mode }, `TFI Friday alert spectacle set to ${MOTION_LABELS[mode]}.`)}>{MOTION_LABELS[mode]}</button>)}</div><small>Classic preserves the current alert. Animated adds the shape-following energy rail. Maximum adds the rail, arrival punch and outward burst. Reduced-motion freezes all alert movement automatically.</small><button type="button" className="ffs-button secondary" disabled={busy || !rehearsalReady || snapshot?.selectedStyle !== 'tfi-friday'} title={snapshot?.selectedStyle !== 'tfi-friday' ? 'Select TFI Friday as the current stream style before testing its alert motion.' : 'Enable Rehearsal Mode to run an isolated simulated follow alert using the selected motion setting.'} onClick={() => void runTest('follow')}><Play size={13} /> Test Alert Motion</button></div><div className="ffs-tfi-ambient-control"><div><span>Ambient rainbow shimmer</span><small>Subtle 1.35s full-screen pass every 3–4 minutes. Pauses during alerts and is suppressed by reduced-motion.</small></div><button type="button" className={ambientShimmerEnabled ? 'selected' : ''} disabled={busy} onClick={() => void apply({ style: 'tfi-friday', ambientShimmerEnabled: !ambientShimmerEnabled }, ambientShimmerEnabled ? 'TFI Friday ambient shimmer disabled.' : 'TFI Friday ambient shimmer enabled.')}>{ambientShimmerEnabled ? 'Shimmer On' : 'Shimmer Off'}</button><button type="button" className="ffs-button secondary" title="Open an isolated one-off shimmer preview in a new tab; does not trigger OBS or change Twitch state." onClick={() => window.open('/overlays/tfi-friday-ambient?preview=1', '_blank', 'noopener,noreferrer')}><Play size={13} /> Test Shimmer</button><code>/overlays/tfi-friday-ambient · 1920 × 1080</code></div></div> : null}
          <div className="ffs-alert-duration-control">
            <label><span>Standard alert duration</span><div><input type="number" min="1" max="30" step="0.5" value={durationSeconds} aria-invalid={!durationValid} disabled={busy} onChange={(event) => setDurationSeconds(event.target.value)} /><b>seconds</b></div></label>
            <button type="button" className="ffs-button secondary" disabled={busy || !durationValid || durationMs === selectedDuration} onClick={() => void apply({ style: editStyle, alertDurationMs: durationMs }, `${STYLE_LABELS[editStyle]} standard alert duration updated.`)}>Save</button>
            <small>Follow, sub, resub, gifts and Bits. Raids stay 20s; shoutouts and Channel Points stay 10s.</small>
          </div>
        </div>

        <div className="ffs-alert-slot-list">
          {ALERTS.map((definition) => {
            const slot = selectedSlots.find((item) => item.slot === definition.type);
            return <div className="ffs-alert-slot-row" key={definition.type}>
              <div className="identity"><strong>{definition.label}</strong><span>{definition.description}</span></div>
              <label><span>Sound</span><select value={slot?.soundId ?? ''} disabled={busy} onChange={(event) => void apply({ style: editStyle, slot: definition.type, soundId: event.target.value || null }, `${definition.label} sound updated for ${STYLE_LABELS[editStyle]}.`)}><option value="">No alert sound</option>{snapshot?.sounds.map((sound) => <option key={sound.id} value={sound.id} disabled={!sound.enabled}>{sound.name}{!sound.enabled ? ' — disabled' : !sound.soundboardVisible ? ' — effect only' : ''}</option>)}</select></label>
              <label><span>Volume</span><select value={slot?.volumeOverride === null || slot?.volumeOverride === undefined ? '' : String(slot.volumeOverride)} disabled={busy} onChange={(event) => void apply({ style: editStyle, slot: definition.type, volumeOverride: event.target.value === '' ? null : Number(event.target.value) }, `${definition.label} volume updated for ${STYLE_LABELS[editStyle]}.`)}><option value="">Sound default</option><option value="0.25">25%</option><option value="0.5">50%</option><option value="0.75">75%</option><option value="1">100%</option></select></label>
              {definition.testable ? <button type="button" className="ffs-button secondary" disabled={busy || !rehearsalReady} title={rehearsalReady ? 'Queue an isolated simulated alert.' : 'Use the Rehearsal controls below to unlock testing.'} onClick={() => void runTest(definition.type as TestableAlertType)}><Play size={13} /> Test</button> : <span className="ffs-alert-no-test">Test below</span>}
            </div>;
          })}
        </div>
      </section>

      <div className="ffs-alert-lower-grid">
        <section className="ffs-workspace-card ffs-channel-points-card">
          <header><div><small>CHANNEL POINTS</small><h3>Themed reward trigger</h3></div><span>{snapshot?.config.channelPoints.scopeReady ? 'Twitch rewards available' : 'Authorisation required'}</span></header>
          <div className="ffs-channel-points-row">
            <label><span>Exact custom reward</span><select value={snapshot?.config.channelPoints.rewardId ?? ''} disabled={busy || !snapshot?.config.channelPoints.scopeReady} onChange={(event) => void apply({ redemptionRewardId: event.target.value || null }, event.target.value ? 'Themed Channel Points reward updated.' : 'Themed Channel Points reward mapping cleared.')}><option value="">No reward mapped</option>{snapshot?.config.channelPoints.rewardId && !snapshot.config.channelPoints.rewards.some((reward) => reward.id === snapshot.config.channelPoints.rewardId) ? <option value={snapshot.config.channelPoints.rewardId}>{snapshot.config.channelPoints.rewardTitle ?? 'Configured reward unavailable'}</option> : null}{snapshot?.config.channelPoints.rewards.map((reward) => <option value={reward.id} key={reward.id} disabled={!reward.enabled || reward.paused || !reward.inStock}>{reward.title} · {reward.cost.toLocaleString('en-GB')} points{!reward.enabled ? ' — disabled' : reward.paused ? ' — paused' : !reward.inStock ? ' — unavailable' : ''}</option>)}</select></label>
            <p>{snapshot?.config.channelPoints.lastError ?? (snapshot?.config.channelPoints.rewardId ? `Mapped by Twitch reward ID: ${snapshot.config.channelPoints.rewardId}` : 'Other Channel Points rewards may still be recorded by Twitch, but only this exact reward enters the themed effect pipeline.')}</p>
          </div>
        </section>

        <section className={`ffs-workspace-card ffs-alert-test-card ${rehearsalReady ? 'ready' : ''}`}>
          <header><div><small>SAFE TESTING</small><h3>Rehearsal controls</h3></div>{rehearsalReady ? <span className="ffs-status good">READY</span> : <span className="ffs-status warning">LOCKED</span>}</header>
          <p className="ffs-workspace-note">Tests and replays are simulations; they do not fabricate Twitch events.</p>
          <div className="ffs-alert-rehearsal-actions">
            <button type="button" className={`ffs-button ${snapshot?.rehearsalEnabled ? 'secondary' : 'primary'}`} disabled={busy || !snapshot} onClick={() => void applyModes({ rehearsalEnabled: !(snapshot?.rehearsalEnabled ?? false) }, snapshot?.rehearsalEnabled ? 'Rehearsal Mode disabled.' : 'Rehearsal Mode enabled.')}>{snapshot?.rehearsalEnabled ? 'Disable Rehearsal Mode' : 'Enable Rehearsal Mode'}</button>
            {snapshot?.rehearsalSuppressed ? <button type="button" className="ffs-button warning" disabled={busy} onClick={() => void applyModes({ suppressedFeatures: snapshot.suppressedFeatures.filter((feature) => feature !== 'rehearsal-actions') }, 'Safe Mode now permits rehearsal actions.')}>Allow Rehearsal Tests</button> : null}
            <button
              type="button"
              className="ffs-button primary"
              disabled={busy || !rehearsalReady || !snapshot}
              title={rehearsalReady ? `Test a simulated redemption using ${snapshot ? STYLE_LABELS[snapshot.selectedStyle] : 'the active theme'}.` : 'Enable Rehearsal Mode and allow rehearsal actions in Safe Mode first.'}
              onClick={() => void runTest('redemption')}
            ><Play size={13} /> Test Redemption</button>
          </div>
          <small className="ffs-alert-rehearsal-state">Uses the active theme: <strong>{snapshot ? STYLE_LABELS[snapshot.selectedStyle] : '—'}</strong> · No Twitch redemption or viewer points involved. OBS/overlay must be connected to see the graphic.</small>
          <small className="ffs-alert-rehearsal-state">Rehearsal: {snapshot?.rehearsalEnabled ? 'ON' : 'OFF'} · Safe Mode: {snapshot?.safeModeEnabled ? 'ON' : 'OFF'}{snapshot?.rehearsalSuppressed ? ' · rehearsal actions suppressed' : ''}</small>
        </section>

        <section className="ffs-workspace-card ffs-alert-history-card">
          <header><div><small>DELIVERY HISTORY</small><h3>Recent alerts</h3></div><strong>{visibleHistory.length.toLocaleString('en-GB')}</strong></header>
          <div className="ffs-alert-history-toolbar">
            <label className="ffs-search-field"><Search size={15} /><input type="search" value={historySearch} onChange={(event) => setHistorySearch(event.target.value)} placeholder="Search alert, viewer, status or error" /></label>
            <div className="ffs-alert-filter-buttons">{([['all', 'All'], ['attention', 'Needs Attention'], ['twitch', 'Twitch'], ['simulation', 'Simulations']] as const).map(([value, label]) => <button type="button" key={value} className={historyFilter === value ? 'selected' : ''} onClick={() => setHistoryFilter(value)}>{label}</button>)}</div>
          </div>
          <div className="ffs-alert-history-list" data-local-scroll="true">
            {visibleHistory.map((alert) => {
              const problem = alertProblem(alert);
              return <div className={problem ? 'problem' : ''} key={alert.id}>
                <div className="main"><strong>{alertLabel(alert.alert_type)} · {alert.user_name ?? alert.user_login ?? 'Unknown viewer'}</strong><span>{formatWhen(alert.created_at)} · {STYLE_LABELS[alert.style]} · {alert.simulated ? 'SIMULATION' : 'TWITCH'}</span>{problem ? <p><AlertTriangle size={12} /> {problem}</p> : null}</div>
                <div className="delivery"><span>Overall</span><strong>{alert.status}</strong><small>Visual {alert.visual_status} · Audio {alert.audio_status ?? 'not configured'}</small></div>
                <button type="button" className="ffs-button secondary" disabled={busy || !rehearsalReady} onClick={() => void runReplay(alert)}><RotateCcw size={13} /> Replay</button>
              </div>;
            })}
            {visibleHistory.length === 0 ? <div className="ffs-empty-state">No alerts match the current filter.</div> : null}
          </div>
        </section>
      </div>
    </div>
  );
}
